import React from 'react';
import { Link } from 'react-router-dom';
import { Activity, RefreshCw } from 'lucide-react';

import { formatMultiple, formatShares, statusLabel } from '../../utils/subscriptionMapper';

const COLUMN_COUNT = 10;

const StatusBadge = ({ row }) => {
  switch (row.phase) {
    case 'open':
      return (
        <span className="inline-flex items-center gap-1 text-emerald-700 bg-emerald-50 border border-emerald-200/80 font-bold text-xs px-2.5 py-1 rounded-lg">
          <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
          <span>{statusLabel(row)}</span>
        </span>
      );
    case 'closing_today':
      return (
        <span className="inline-flex items-center gap-1 text-rose-700 bg-rose-50 border border-rose-200/80 font-bold text-xs px-2.5 py-1 rounded-lg">
          <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-pulse" />
          <span>{statusLabel(row)}</span>
        </span>
      );
    case 'upcoming':
      return (
        <span className="text-blue-700 bg-blue-50 border border-blue-200 font-medium text-xs px-2.5 py-1 rounded-lg">
          {statusLabel(row)}
        </span>
      );
    default:
      return (
        <span className="text-slate-500 bg-slate-100 border border-slate-200 font-medium text-xs px-2.5 py-1 rounded-lg">
          {statusLabel(row)}
        </span>
      );
  }
};

const SkeletonRows = () =>
  Array.from({ length: 5 }).map((_, idx) => (
    <tr key={`sub-skel-${idx}`} className="animate-pulse">
      <td className="py-4 px-4 sm:px-6">
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-lg bg-slate-200" />
          <div className="space-y-1.5 flex-1">
            <div className="w-40 h-4 bg-slate-200 rounded" />
            <div className="w-24 h-3 bg-slate-100 rounded" />
          </div>
        </div>
      </td>
      <td className="py-4 px-4"><div className="w-20 h-4 bg-slate-200 rounded" /></td>
      <td className="py-4 px-3"><div className="w-12 h-4 bg-slate-200 rounded ml-auto" /></td>
      <td className="py-4 px-3 hidden lg:table-cell"><div className="w-12 h-4 bg-slate-200 rounded ml-auto" /></td>
      <td className="py-4 px-3 hidden lg:table-cell"><div className="w-12 h-4 bg-slate-200 rounded ml-auto" /></td>
      <td className="py-4 px-3"><div className="w-12 h-4 bg-slate-200 rounded ml-auto" /></td>
      <td className="py-4 px-3"><div className="w-12 h-4 bg-slate-200 rounded ml-auto" /></td>
      <td className="py-4 px-4"><div className="w-14 h-5 bg-slate-200 rounded ml-auto" /></td>
      <td className="py-4 px-4 hidden md:table-cell"><div className="w-20 h-4 bg-slate-200 rounded ml-auto" /></td>
      <td className="py-4 px-4 sm:px-6"><div className="w-20 h-6 bg-slate-200 rounded-lg mx-auto" /></td>
    </tr>
  ));

const Multiple = ({ value }) => (value === null ? <span className="text-slate-300">—</span> : formatMultiple(value));

export const SubscriptionTable = ({
  rows,
  totalCount,
  boardTotal,
  page,
  pageSize,
  totalPages,
  onPageChange,
  isLoading,
  isFailed,
  error,
  onRetry,
  onClearSearch,
}) => {
  const firstShown = totalCount === 0 ? 0 : (page - 1) * pageSize + 1;
  const lastShown = Math.min(page * pageSize, totalCount);

  let body;
  if (isLoading) {
    body = <SkeletonRows />;
  } else if (isFailed) {
    body = (
      <tr>
        <td colSpan={COLUMN_COUNT} className="py-12 text-center">
          <div className="max-w-md mx-auto space-y-3 px-4">
            <div className="w-12 h-12 rounded-full bg-rose-50 text-rose-600 flex items-center justify-center mx-auto">
              <RefreshCw className="w-5 h-5" />
            </div>
            <p className="text-slate-700 font-semibold text-sm">Subscription data is temporarily unavailable</p>
            <p className="text-slate-400 text-xs">{error}</p>
            <button
              type="button"
              onClick={onRetry}
              className="px-4 py-1.5 bg-indigo-600 text-white text-xs font-semibold rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Retry Loading
            </button>
          </div>
        </td>
      </tr>
    );
  } else if (rows.length === 0) {
    body = (
      <tr>
        <td colSpan={COLUMN_COUNT} className="py-12 text-center">
          <div className="max-w-md mx-auto space-y-2 text-slate-500 px-4">
            <Activity className="w-8 h-8 mx-auto text-slate-300" />
            <p className="font-semibold text-slate-700 text-sm">
              {boardTotal === 0 ? 'No IPOs in this segment are open or opening soon' : 'No IPOs match your search'}
            </p>
            {boardTotal > 0 && (
              <button type="button" onClick={onClearSearch} className="mt-2 text-xs text-indigo-600 font-semibold hover:underline">
                Clear Search
              </button>
            )}
          </div>
        </td>
      </tr>
    );
  } else {
    body = rows.map((row) => (
      <tr key={row.id} className="hover:bg-slate-50/70 transition-colors">
        {/* Company & Window */}
        <td className="py-3.5 px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 rounded-lg bg-indigo-50 text-indigo-700 font-bold text-xs flex items-center justify-center border border-indigo-100 flex-shrink-0">
              {row.company.charAt(0)}
            </div>
            <div>
              <div className="font-bold text-slate-900 flex items-center gap-1.5">
                {row.symbol ? (
                  <Link to={`/ipo-subscription/${encodeURIComponent(row.symbol)}`} className="hover:text-[#008080]">
                    {row.company}
                  </Link>
                ) : (
                  <span>{row.company}</span>
                )}
                {row.phase === 'closing_today' && (
                  <span className="text-[10px] bg-rose-50 text-rose-600 border border-rose-200 px-1.5 py-0.2 rounded font-semibold whitespace-nowrap">
                    Closing Today
                  </span>
                )}
              </div>
              <div className="text-xs text-slate-400">
                {[row.symbol, `${row.boardLabel} Issue`, row.windowLabel].filter(Boolean).join(' • ')}
                {row.error && <span className="text-amber-600"> • NSE data unavailable</span>}
              </div>
            </div>
          </div>
        </td>

        {/* Price Band */}
        <td className="py-3.5 px-4 font-semibold text-slate-700 whitespace-nowrap">{row.priceBand}</td>

        {/* QIB */}
        <td className="py-3.5 px-3 text-right font-semibold text-slate-800 whitespace-nowrap">
          <Multiple value={row.qib} />
        </td>

        {/* sNII */}
        <td className="py-3.5 px-3 text-right text-slate-600 hidden lg:table-cell whitespace-nowrap">
          <Multiple value={row.snii} />
        </td>

        {/* bNII */}
        <td className="py-3.5 px-3 text-right text-slate-600 hidden lg:table-cell whitespace-nowrap">
          <Multiple value={row.bnii} />
        </td>

        {/* Total NII */}
        <td className="py-3.5 px-3 text-right font-bold text-indigo-700 whitespace-nowrap">
          <Multiple value={row.nii} />
        </td>

        {/* Retail (RII) */}
        <td className="py-3.5 px-3 text-right font-bold text-emerald-600 whitespace-nowrap">
          <Multiple value={row.retail} />
        </td>

        {/* Total (x) */}
        <td className="py-3.5 px-4 text-right font-black text-slate-900 bg-indigo-50/30 whitespace-nowrap">
          {row.total === null ? (
            <span className="text-slate-300">—</span>
          ) : (
            <span className="text-sm font-extrabold text-indigo-900 bg-indigo-100/70 px-2 py-0.5 rounded">
              {formatMultiple(row.total)}
            </span>
          )}
        </td>

        {/* Shares Bid / Offered */}
        <td className="py-3.5 px-4 text-right font-mono text-slate-600 hidden md:table-cell whitespace-nowrap">
          {row.sharesBid === null ? (
            <span className="text-slate-300">—</span>
          ) : (
            <>
              <div>{formatShares(row.sharesBid)}</div>
              {row.sharesOffered ? (
                <div className="text-[10px] text-slate-400">of {formatShares(row.sharesOffered)}</div>
              ) : null}
            </>
          )}
        </td>

        {/* Status */}
        <td className="py-3.5 px-4 sm:px-6 text-center whitespace-nowrap">
          <StatusBadge row={row} />
        </td>
      </tr>
    ));
  }

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs overflow-hidden my-6">
      {/* Table Container */}
      <div className="overflow-x-auto scrollbar-none">
        <table className="w-full text-left border-collapse text-xs sm:text-sm">
          <thead>
            <tr className="bg-slate-50/90 border-b border-slate-200/80 text-[11px] font-bold text-slate-500 uppercase tracking-wider">
              <th className="py-3.5 px-4 sm:px-6">Company & Window</th>
              <th className="py-3.5 px-4">Price Band</th>
              <th className="py-3.5 px-3 text-right">QIB (x)</th>
              <th className="py-3.5 px-3 text-right hidden lg:table-cell">sNII (&lt;₹10L)</th>
              <th className="py-3.5 px-3 text-right hidden lg:table-cell">bNII (&gt;₹10L)</th>
              <th className="py-3.5 px-3 text-right font-black text-slate-700">Total NII</th>
              <th className="py-3.5 px-3 text-right text-indigo-600 font-bold">Retail (RII)</th>
              <th className="py-3.5 px-4 text-right font-black text-slate-900 bg-indigo-50/30">Total (x)</th>
              <th className="py-3.5 px-4 text-right hidden md:table-cell">Shares Bid</th>
              <th className="py-3.5 px-4 sm:px-6 text-center">Status</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">{body}</tbody>
        </table>
      </div>

      {/* Table Pagination Bar */}
      {!isLoading && !isFailed && totalCount > 0 && (
        <div className="py-3 px-4 sm:px-6 bg-slate-50/80 border-t border-slate-100 flex flex-col sm:flex-row sm:items-center justify-between gap-3 text-xs text-slate-500">
          <div>
            Showing {firstShown} to {lastShown} of {totalCount} {totalCount === 1 ? 'IPO' : 'IPOs'}
          </div>

          {totalPages > 1 && (
            <div className="flex items-center gap-1.5 self-center sm:self-auto">
              <button
                type="button"
                onClick={() => onPageChange(page - 1)}
                disabled={page === 1}
                className="px-2.5 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-50"
              >
                Prev
              </button>
              {Array.from({ length: totalPages }, (_, idx) => idx + 1).map((pageNumber) => (
                <button
                  key={pageNumber}
                  type="button"
                  onClick={() => onPageChange(pageNumber)}
                  className={`w-7 h-7 rounded-md font-medium text-xs transition-colors ${
                    page === pageNumber
                      ? 'bg-indigo-600 text-white'
                      : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'
                  }`}
                >
                  {pageNumber}
                </button>
              ))}
              <button
                type="button"
                onClick={() => onPageChange(page + 1)}
                disabled={page === totalPages}
                className="px-2.5 py-1 rounded-md border border-slate-200 bg-white hover:bg-slate-50 text-slate-600 disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SubscriptionTable;
